import { ref, computed, watch, onBeforeUnmount } from "vue";
import { currentLang, setAppLang, messages, normalizeLang } from "@/i18n";

export function usePageI18n() {
  // Local ref initialized from centralized currentLang
  const lang = ref(currentLang.value || "ar");

  // Keep local ref tightly synchronized with the global reactive currentLang
  const unwatch = watch(
    () => currentLang.value,
    (next) => {
      if (next && lang.value !== next) {
        lang.value = next;
      }
    },
    { immediate: true }
  );

  const _langHandler = (e) => {
    const next = normalizeLang(e?.detail || e);
    if (next && lang.value !== next) {
      lang.value = next;
    }
    if (next && currentLang.value !== next) {
      currentLang.value = next;
    }
  };

  if (typeof window !== "undefined") {
    window.addEventListener("app:lang", _langHandler, { passive: true });
    window.addEventListener("app:lang-changed", _langHandler, { passive: true });
  }

  // Fallback observer on <html lang/dir>
  let _langObserver = null;
  if (typeof MutationObserver !== "undefined" && typeof document !== "undefined") {
    _langObserver = new MutationObserver(() => {
      const htmlLang = normalizeLang(
        document.documentElement.lang || localStorage.getItem("app_lang") || "ar"
      );
      if (htmlLang !== lang.value) {
        lang.value = htmlLang;
      }
      if (htmlLang !== currentLang.value) {
        currentLang.value = htmlLang;
      }
    });

    _langObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["lang", "dir"],
    });
  }

  onBeforeUnmount(() => {
    unwatch();
    if (typeof window !== "undefined") {
      window.removeEventListener("app:lang", _langHandler);
      window.removeEventListener("app:lang-changed", _langHandler);
    }
    if (_langObserver) {
      _langObserver.disconnect();
    }
  });

  const isRtl = computed(() => lang.value === "ar");
  const dir = computed(() => (lang.value === "ar" ? "rtl" : "ltr"));

  function t(key, vars = null) {
    const l = lang.value;
    const base = messages[l];
    if (!base) return key;

    let val = base[key];
    if (val == null && key && typeof key === "string" && key.includes(".")) {
      val = key.split(".").reduce((acc, part) => acc?.[part], base);
    }
    if (val == null) {
      const fallbackBase = messages[l === "ar" ? "en" : "ar"];
      val = fallbackBase?.[key];
    }
    if (val == null) return key;

    if (vars && typeof val === "string") {
      return val.replace(/\{(\w+)\}/g, (_, k) => (vars?.[k] != null ? vars[k] : `{${k}}`));
    }
    return val;
  }

  return { lang, t, setLang: setAppLang, isRtl, dir };
}
